// ===========================================
// Gamification Hooks
// ===========================================
// React Query hooks for gamification features

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  getGamificationLevels,
  getMyProgress,
  getStudentProgress,
  getAllProgress,
  advanceStudentLevel,
  getLeaderboard,
  getMyAchievements,
  getStudentAchievements,
  getRecentAchievements,
  awardAchievement,
  awardSpecialAchievement,
  revokeAchievement,
  getAchievementDefinitions,
  getAchievementDefinition,
  createAchievementDefinition,
  updateAchievementDefinition,
  deleteAchievementDefinition,
  getPieces,
  getPiece,
  createPiece,
  updatePiece,
  deletePiece,
  addSkillToPiece,
  updateSkill,
  deleteSkill,
  reorderSkills,
  uploadLessonPlan,
  clearLessonPlan,
  getMyPieces,
  getStudentPieces,
  getStudentPieceDetail,
  assignPiece,
  unassignPiece,
  updateSkillProgress,
  startSkill,
  completeSkill,
  getLessonPieces,
  assignPieceToLesson,
  removePieceFromLesson,
  toggleLessonPieceCurrent,
  getStudentLessonPieces,
  getClassPieceProgress,
} from '../api/gamification.api';
import {
  LeaderboardParams,
  CreateAchievementDefinitionData,
  UpdateAchievementDefinitionData,
  AwardAchievementData,
  AwardSpecialAchievementData,
  AchievementDefinitionFilters,
  StudentAchievementFilters,
  RecentAchievementsFilters,
  CreatePieceData,
  UpdatePieceData,
  PieceFilters,
  CreateSkillData,
  UpdateSkillData,
  ReorderSkillsData,
  AssignPieceData,
  StudentPieceFilters,
  UpdateSkillProgressData,
} from '../types/gamification.types';

// ===========================================
// QUERY KEY FACTORY
// ===========================================

export const gamificationKeys = {
  all: ['gamification'] as const,
  // Levels
  levels: () => [...gamificationKeys.all, 'levels'] as const,
  // Progress
  progress: () => [...gamificationKeys.all, 'progress'] as const,
  myProgress: () => [...gamificationKeys.progress(), 'me'] as const,
  studentProgress: (studentId: string) => [...gamificationKeys.progress(), studentId] as const,
  allProgress: (params?: { page?: number; limit?: number; level?: string }) =>
    [...gamificationKeys.progress(), 'all', params] as const,
  leaderboard: (params?: LeaderboardParams) =>
    [...gamificationKeys.progress(), 'leaderboard', params] as const,
  // Achievements
  achievements: () => [...gamificationKeys.all, 'achievements'] as const,
  myAchievements: (params?: StudentAchievementFilters) =>
    [...gamificationKeys.achievements(), 'me', params] as const,
  studentAchievements: (studentId: string, params?: StudentAchievementFilters) =>
    [...gamificationKeys.achievements(), studentId, params] as const,
  recentAchievements: (params?: RecentAchievementsFilters) =>
    [...gamificationKeys.achievements(), 'recent', params] as const,
  // Achievement Definitions
  definitions: () => [...gamificationKeys.all, 'definitions'] as const,
  definitionsList: (params?: AchievementDefinitionFilters) =>
    [...gamificationKeys.definitions(), 'list', params] as const,
  definition: (id: string) => [...gamificationKeys.definitions(), id] as const,
  // Pieces
  pieces: () => [...gamificationKeys.all, 'pieces'] as const,
  piecesList: (params?: PieceFilters) => [...gamificationKeys.pieces(), 'list', params] as const,
  piece: (pieceId: string) => [...gamificationKeys.pieces(), pieceId] as const,
  // Student Pieces
  studentPieces: () => [...gamificationKeys.all, 'student-pieces'] as const,
  myPieces: (params?: StudentPieceFilters) =>
    [...gamificationKeys.studentPieces(), 'me', params] as const,
  studentPiecesList: (studentId: string, params?: StudentPieceFilters) =>
    [...gamificationKeys.studentPieces(), studentId, params] as const,
  studentPieceDetail: (studentPieceId: string) =>
    [...gamificationKeys.studentPieces(), 'detail', studentPieceId] as const,
  // Lesson Pieces
  lessonPieces: () => [...gamificationKeys.all, 'lesson-pieces'] as const,
  lessonPiecesList: (lessonId: string) =>
    [...gamificationKeys.lessonPieces(), lessonId] as const,
  studentLessonPieces: (studentId: string) =>
    [...gamificationKeys.lessonPieces(), 'student', studentId] as const,
  classPieceProgress: (lessonId: string, pieceId: string) =>
    [...gamificationKeys.lessonPieces(), 'class-progress', lessonId, pieceId] as const,
};

// ===========================================
// LEVEL HOOKS
// ===========================================

/**
 * Get school's gamification-enabled program levels
 */
export function useGamificationLevels() {
  return useQuery({
    queryKey: gamificationKeys.levels(),
    queryFn: getGamificationLevels,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ===========================================
// PROGRESS HOOKS
// ===========================================

/**
 * Get current user's gamification progress
 */
export function useMyProgress() {
  return useQuery({
    queryKey: gamificationKeys.myProgress(),
    queryFn: getMyProgress,
  });
}

/**
 * Get a specific student's progress (for teachers/admins)
 */
export function useStudentProgress(studentId: string) {
  return useQuery({
    queryKey: gamificationKeys.studentProgress(studentId),
    queryFn: () => getStudentProgress(studentId),
    enabled: !!studentId,
  });
}

/**
 * Get all students' progress (admin - paginated)
 */
export function useAllProgress(params?: {
  page?: number;
  limit?: number;
  level?: string;
  sortBy?: string;
}) {
  return useQuery({
    queryKey: gamificationKeys.allProgress(params),
    queryFn: () => getAllProgress(params),
  });
}

/**
 * Get school leaderboard
 */
export function useLeaderboard(params?: LeaderboardParams) {
  return useQuery({
    queryKey: gamificationKeys.leaderboard(params),
    queryFn: () => getLeaderboard(params),
  });
}

/**
 * Advance a student's level
 */
export function useAdvanceLevel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (studentId: string) => advanceStudentLevel(studentId),
    onSuccess: (_data, studentId) => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.studentProgress(studentId) });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.allProgress() });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.leaderboard() });
    },
  });
}

// ===========================================
// ACHIEVEMENT HOOKS
// ===========================================

/**
 * Get current user's achievements
 */
export function useMyAchievements(params?: StudentAchievementFilters) {
  return useQuery({
    queryKey: gamificationKeys.myAchievements(params),
    queryFn: () => getMyAchievements(params),
  });
}

/**
 * Get a student's achievements
 */
export function useStudentAchievements(studentId: string, params?: StudentAchievementFilters) {
  return useQuery({
    queryKey: gamificationKeys.studentAchievements(studentId, params),
    queryFn: () => getStudentAchievements(studentId, params),
    enabled: !!studentId,
  });
}

/**
 * Get recent school-wide achievements
 */
export function useRecentAchievements(params?: RecentAchievementsFilters) {
  return useQuery({
    queryKey: gamificationKeys.recentAchievements(params),
    queryFn: () => getRecentAchievements(params),
  });
}

/**
 * Award an achievement to a student
 */
export function useAwardAchievement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, data }: { studentId: string; data: AwardAchievementData }) =>
      awardAchievement(studentId, data),
    onSuccess: (_data, { studentId }) => {
      queryClient.invalidateQueries({
        queryKey: gamificationKeys.studentAchievements(studentId),
      });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.recentAchievements() });
    },
  });
}

/**
 * Award a special (custom) achievement to a student
 */
export function useAwardSpecialAchievement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, data }: { studentId: string; data: AwardSpecialAchievementData }) =>
      awardSpecialAchievement(studentId, data),
    onSuccess: (_data, { studentId }) => {
      queryClient.invalidateQueries({
        queryKey: gamificationKeys.studentAchievements(studentId),
      });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.recentAchievements() });
    },
  });
}

/**
 * Revoke an achievement
 */
export function useRevokeAchievement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (achievementId: string) => revokeAchievement(achievementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.achievements() });
    },
  });
}

// ===========================================
// ACHIEVEMENT DEFINITION HOOKS
// ===========================================

/**
 * Get all achievement definitions
 */
export function useAchievementDefinitions(params?: AchievementDefinitionFilters) {
  return useQuery({
    queryKey: gamificationKeys.definitionsList(params),
    queryFn: () => getAchievementDefinitions(params),
  });
}

/**
 * Get a single achievement definition
 */
export function useAchievementDefinition(id: string) {
  return useQuery({
    queryKey: gamificationKeys.definition(id),
    queryFn: () => getAchievementDefinition(id),
    enabled: !!id,
  });
}

/**
 * Create an achievement definition
 */
export function useCreateAchievementDefinition() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateAchievementDefinitionData) => createAchievementDefinition(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.definitions() });
      enqueueSnackbar('Achievement definition created successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to create achievement definition';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update an achievement definition
 */
export function useUpdateAchievementDefinition() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAchievementDefinitionData }) =>
      updateAchievementDefinition(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.definition(id) });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.definitions() });
      enqueueSnackbar('Achievement definition updated successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to update achievement definition';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Delete an achievement definition
 */
export function useDeleteAchievementDefinition() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => deleteAchievementDefinition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.definitions() });
      enqueueSnackbar('Achievement definition deleted successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to delete achievement definition';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// PIECE LIBRARY HOOKS
// ===========================================

/**
 * Get all pieces with optional filters
 */
export function usePieces(params?: PieceFilters) {
  return useQuery({
    queryKey: gamificationKeys.piecesList(params),
    queryFn: () => getPieces(params),
  });
}

/**
 * Get a single piece with skills
 */
export function usePiece(pieceId: string) {
  return useQuery({
    queryKey: gamificationKeys.piece(pieceId),
    queryFn: () => getPiece(pieceId),
    enabled: !!pieceId,
  });
}

/**
 * Create a new piece
 */
export function useCreatePiece() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreatePieceData) => createPiece(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.pieces() });
      enqueueSnackbar('Piece created successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to create piece';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update a piece
 */
export function useUpdatePiece() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ pieceId, data }: { pieceId: string; data: UpdatePieceData }) =>
      updatePiece(pieceId, data),
    onSuccess: (_data, { pieceId }) => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.piece(pieceId) });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.pieces() });
      enqueueSnackbar('Piece updated successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to update piece';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Delete a piece
 */
export function useDeletePiece() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (pieceId: string) => deletePiece(pieceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.pieces() });
      enqueueSnackbar('Piece deleted successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to delete piece';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// PIECE SKILL HOOKS
// ===========================================

/**
 * Add a skill to a piece
 */
export function useAddSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pieceId, data }: { pieceId: string; data: CreateSkillData }) =>
      addSkillToPiece(pieceId, data),
    onSuccess: (_data, { pieceId }) => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.piece(pieceId) });
    },
  });
}

/**
 * Update a skill
 */
export function useUpdateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ skillId, data }: { skillId: string; data: UpdateSkillData }) =>
      updateSkill(skillId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.pieces() });
    },
  });
}

/**
 * Delete a skill
 */
export function useDeleteSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (skillId: string) => deleteSkill(skillId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.pieces() });
    },
  });
}

/**
 * Reorder skills within a piece
 */
export function useReorderSkills() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pieceId, data }: { pieceId: string; data: ReorderSkillsData }) =>
      reorderSkills(pieceId, data),
    onSuccess: (_data, { pieceId }) => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.piece(pieceId) });
    },
  });
}

// ===========================================
// LESSON PLAN HOOKS
// ===========================================

/**
 * Upload a lesson-plan PDF and attach it to a piece, or to a stage when skillId is given.
 */
export function useUploadLessonPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pieceId, file, skillId }: { pieceId: string; file: File; skillId?: string }) =>
      uploadLessonPlan(pieceId, file, skillId),
    onSuccess: (_data, { pieceId }) => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.piece(pieceId) });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.pieces() });
    },
  });
}

/**
 * Clear a lesson plan on a piece, or on a stage when skillId is given.
 */
export function useClearLessonPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pieceId, skillId }: { pieceId: string; skillId?: string }) =>
      clearLessonPlan(pieceId, skillId),
    onSuccess: (_data, { pieceId }) => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.piece(pieceId) });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.pieces() });
    },
  });
}

// ===========================================
// STUDENT PIECE HOOKS
// ===========================================

/**
 * Get current user's assigned pieces
 */
export function useMyPieces(params?: StudentPieceFilters) {
  return useQuery({
    queryKey: gamificationKeys.myPieces(params),
    queryFn: () => getMyPieces(params),
  });
}

/**
 * Get a student's assigned pieces
 */
export function useStudentPieces(studentId: string, params?: StudentPieceFilters) {
  return useQuery({
    queryKey: gamificationKeys.studentPiecesList(studentId, params),
    queryFn: () => getStudentPieces(studentId, params),
    enabled: !!studentId,
  });
}

/**
 * Get detailed student piece with skill progress
 */
export function useStudentPieceDetail(studentPieceId: string) {
  return useQuery({
    queryKey: gamificationKeys.studentPieceDetail(studentPieceId),
    queryFn: () => getStudentPieceDetail(studentPieceId),
    enabled: !!studentPieceId,
  });
}

/**
 * Assign a piece to a student
 */
export function useAssignPiece() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, data }: { studentId: string; data: AssignPieceData }) =>
      assignPiece(studentId, data),
    onSuccess: (_data, { studentId }) => {
      queryClient.invalidateQueries({
        queryKey: gamificationKeys.studentPiecesList(studentId),
      });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.studentProgress(studentId) });
    },
  });
}

/**
 * Unassign a piece from a student
 */
export function useUnassignPiece() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (studentPieceId: string) => unassignPiece(studentPieceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.studentPieces() });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.progress() });
    },
  });
}

// ===========================================
// SKILL PROGRESS HOOKS
// ===========================================

/**
 * Update skill progress status
 */
export function useUpdateSkillProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      skillProgressId,
      data,
    }: {
      skillProgressId: string;
      data: UpdateSkillProgressData;
    }) => updateSkillProgress(skillProgressId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.studentPieces() });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.progress() });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.lessonPieces() });
    },
  });
}

/**
 * Mark a skill as in progress
 */
export function useStartSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (skillProgressId: string) => startSkill(skillProgressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.studentPieces() });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.lessonPieces() });
    },
  });
}

/**
 * Mark a skill as complete
 */
export function useCompleteSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ skillProgressId, notes }: { skillProgressId: string; notes?: string }) =>
      completeSkill(skillProgressId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.studentPieces() });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.progress() });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.achievements() });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.lessonPieces() });
    },
  });
}

// ===========================================
// LESSON PIECE HOOKS
// ===========================================

/**
 * Get all pieces assigned to a lesson/class
 */
export function useLessonPieces(lessonId: string) {
  return useQuery({
    queryKey: gamificationKeys.lessonPiecesList(lessonId),
    queryFn: () => getLessonPieces(lessonId),
    enabled: !!lessonId,
  });
}

/**
 * Assign a piece to a lesson/class
 */
export function useAssignPieceToLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lessonId, pieceId }: { lessonId: string; pieceId: string }) =>
      assignPieceToLesson(lessonId, pieceId),
    onSuccess: (_, { lessonId }) => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.lessonPiecesList(lessonId) });
      queryClient.invalidateQueries({ queryKey: gamificationKeys.studentPieces() });
    },
  });
}

/**
 * Remove a piece from a lesson/class
 */
export function useRemovePieceFromLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lessonId, pieceId }: { lessonId: string; pieceId: string }) =>
      removePieceFromLesson(lessonId, pieceId),
    onSuccess: (_, { lessonId }) => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.lessonPiecesList(lessonId) });
    },
  });
}

/**
 * Toggle isCurrent status for a lesson piece
 */
export function useToggleLessonPieceCurrent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lessonPieceId, isCurrent }: { lessonPieceId: string; isCurrent: boolean }) =>
      toggleLessonPieceCurrent(lessonPieceId, isCurrent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gamificationKeys.lessonPieces() });
    },
  });
}

/**
 * Get current and past pieces for a student across enrolled classes
 */
export function useStudentLessonPieces(studentId: string) {
  return useQuery({
    queryKey: gamificationKeys.studentLessonPieces(studentId),
    queryFn: () => getStudentLessonPieces(studentId),
    enabled: !!studentId,
  });
}

/**
 * Get all enrolled students and their skill progress for a class piece
 */
export function useClassPieceProgress(lessonId: string, pieceId: string) {
  return useQuery({
    queryKey: gamificationKeys.classPieceProgress(lessonId, pieceId),
    queryFn: () => getClassPieceProgress(lessonId, pieceId),
    enabled: !!lessonId && !!pieceId,
  });
}
